import { NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UserService } from 'src/app/core/services/user.service';
import { PaButtonComponent } from 'src/app/shared/ui-kit/pa-button/pa-button.component';
import { PaInputOtpComponent } from 'src/app/shared/ui-kit/pa-input-otp/pa-input-otp.component';
import { environment } from 'src/environments/environment';
import { SupportLinkV2Component } from '../../components/support-link/support-link.component';
import { AuthFlowId } from '../../models/auth-flow.models';
import { AuthActionsService } from '../../services/auth-actions.service';
import { AuthFlowService } from '../../services/auth-flow.service';
import { AuthSessionStore } from '../../services/auth-session.store';
import { IosKeyboardHoldService } from '../../services/ios-keyboard-hold.service';
import { ToolsService } from 'src/app/core/services/tools.service';

@Component({
  selector: 'app-confirm-email-step',
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    PaButtonComponent,
    PaInputOtpComponent,
    SupportLinkV2Component,
  ],
  templateUrl: './confirm-email-step.component.html',
  styleUrls: ['../../styles/_step.styles.scss'],
})
export class ConfirmEmailStepComponent implements OnInit, AfterViewInit {
  readonly environment = environment;

  readonly form = new FormGroup({
    code: new FormControl('', { nonNullable: true }),
  });

  private codeValidationMessage = '';
  private isSubmitting = false;

  @ViewChild(PaInputOtpComponent)
  otpInput?: PaInputOtpComponent;

  get showCodeError(): boolean {
    const control = this.form.controls.code;
    return control.hasError('invalidCode') && (control.touched || control.dirty);
  }

  get codeErrorText(): string {
    return this.codeValidationMessage || 'Неверный код подтверждения.';
  }

  readonly store = inject(AuthSessionStore);
  private readonly flow = inject(AuthFlowService);
  private readonly actions = inject(AuthActionsService);
  private readonly user = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly keyboardHold = inject(IosKeyboardHoldService);
  private readonly toolsService = inject(ToolsService);

  ngOnInit(): void {
    this.form.controls.code.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.form.controls.code.disabled) {
          this.clearCodeValidationError();
        }
        if (!this.isCodeSubmitDisabled()) {
          void this.submit();
        }
      });

    this.updateCodeInputAvailability();
  }

  ngAfterViewInit(): void {
    this.focusCodeInput();
  }

  get login(): string {
    return this.store.account().login || this.user.data.email || '';
  }

  get isRegistration(): boolean {
    return this.flow.flowId() === AuthFlowId.Registration;
  }

  isCodeValid(): boolean {
    return /^\d{4}$/.test(this.form.controls.code.value.trim());
  }

  isCodeSubmitDisabled(): boolean {
    return !this.isCodeValid() || this.store.account().attempts === 0;
  }

  back(): void {
    this.flow.goBack();
  }

  onCodeEnter(event: Event): void {
    event.preventDefault();
    if (this.isCodeSubmitDisabled()) {
      return;
    }
    void this.submit();
  }

  async submit(): Promise<void> {
    if (this.isCodeSubmitDisabled() || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const result = await this.actions.confirmCode(
        this.form.controls.code.value,
      );
      if (result.status === 'invalid') {
        this.form.patchValue({ code: '' }, { emitEvent: false });
        this.setCodeValidationError(
          this.store.account().attempts!,
          result.message,
        );
        this.focusCodeInput();
      }
      if (result.status === 'blocked') {
        this.form.patchValue({ code: '' }, { emitEvent: false });
        this.applyCodeAttemptsLimit();
      }
      if (result.status === 'success') {
        this.store.patchAccount({ login: this.toolsService.extractEmail(this.login) });
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  resendCode(): void {
    this.resetCodeAttemptsState();
    this.form.reset({ code: '' });
    void this.actions.resendCode();
  }

  private setCodeValidationError(attempts: number, message?: string): void {
    this.codeValidationMessage =
      message ??
      `Неверный код подтверждения. Осталось ${attempts} ${this.pluralizeAttempts(attempts)}.`;
    const control = this.form.controls.code;
    control.setErrors({ ...(control.errors ?? {}), invalidCode: true });
    control.markAsDirty();
    control.markAsTouched();
  }

  private clearCodeValidationError(): void {
    this.codeValidationMessage = '';
    const control = this.form.controls.code;
    if (!control.hasError('invalidCode')) {
      return;
    }
    const rest = Object.fromEntries(
      Object.entries(control.errors ?? {}).filter(
        ([key]) => key !== 'invalidCode',
      ),
    );
    control.setErrors(Object.keys(rest).length ? rest : null);
  }

  private applyCodeAttemptsLimit(): void {
    this.form.controls.code.disable({ emitEvent: false });
  }

  private resetCodeAttemptsState(): void {
    this.store.patchAccount({ attempts: null });
    this.clearCodeValidationError();
    this.form.controls.code.enable({ emitEvent: false });
  }

  private updateCodeInputAvailability(): void {
    if (this.store.account().attempts === 0) {
      this.form.controls.code.disable({ emitEvent: false });
    }
  }

  private pluralizeAttempts(count: number): string {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return 'попытка';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
      return 'попытки';
    }
    return 'попыток';
  }

  private focusCodeInput(): void {
    const input = this.otpInput?.getFirstInput();
    if (input && !input.disabled) {
      this.keyboardHold.transferTo(input);
      return;
    }
    setTimeout(() => {
      const retry = this.otpInput?.getFirstInput();
      if (retry && !retry.disabled) {
        this.keyboardHold.transferTo(retry);
      }
    }, 50);
  }
}
